import { lazy } from 'react'
import { Navigate } from 'react-router-dom'
import ErrorInsights from 'src/pages/members/DeliveryIntelligence/ErrorInsights'
import { LoopModule } from 'src/services/openApiV2'

const DashboardTrendsOnly = lazy(() => import('src/pages/members/dashboard/subPages/TrendsOnly'))
const DashboardMetricsOnly = lazy(() => import('src/pages/members/dashboard/subPages/MetricsOnly'))
const DashboardErrorOverview = lazy(() => import('src/pages/members/dashboard/subPages/ErrorOverview'))
const DashboardSales = lazy(() => import('src/pages/members/dashboard/subPages/Sales'))
const PlaidAuth = lazy(() => import('src/components/Plaid/PlaidAuth'))

const StorePerformanceIntro = lazy(() => import('src/pages/members/StorePerformance/StorePerformanceIntro'))
const StorePerformanceReport = lazy(() => import('src/pages/members/StorePerformance/StorePerformanceReport'))
const StorePerformanceSubscribeUser = lazy(() => import('src/pages/members/StorePerformance/SubscribeUser'))
const StorePerformanceCadence = lazy(() => import('src/pages/members/StorePerformance/StorePerformanceCadence'))

const WhatsNew = lazy(() => import('src/pages/members/Changelog/WhatsNew'))
const Configurations = lazy(() => import('src/pages/members/Configurations/ConfigurationCenter'))
const StoreAvailabilityReasonsConfigs = lazy(() => import('src/pages/members/StoreAvailabilityReasonsConfigs/StoreAvailabilityReasonsConfigs'))

const DrillDown = lazy(() => import('src/pages/members/DrillDown/DrillDown'))
const Benchmarks = lazy(() => import('src/pages/members/Benchmarks/Benchmarks'))
const OrdersOld = lazy(() => import('src/pages/members/OrdersOld/Orders'))

// OLO
const OloTransactions = lazy(() => import('src/pages/members/OloTransactions/OloTransactions'))
const OloCustomers = lazy(() => import('src/pages/members/OloCustomers/OloCustomers'))
const OloItems = lazy(() => import('src/pages/members/OloItems/OloItems'))

const Leaderboard = lazy(() => import('src/pages/members/Leaderboard'))
const ReportDownload = lazy(() => import('src/pages/members/Reporting/ReportDownload'))

const CompassAnalytics = lazy(() => import('src/pages/members/CompassAnalytics/CompassAnalytics'))


const existingRoutes = [
  // Legacy route redirects - automatically redirect to new /base/* paths
  // Dashboard v1 routes → /base/*
  {
    path: 'dashboard/v1/trends',
    element: <Navigate to="/base/trends" replace />
  }, // 860 pageviews → /base/trends
  {
    path: 'dashboard/v1/metrics',
    element: <Navigate to="/base/metrics" replace />
  }, // 249 pageviews → /base/metrics
  {
    path: 'dashboard/v1/sales',
    element: <Navigate to="/base/sales" replace />
  }, // 101 pageviews → /base/sales
  {
    path: 'dashboard/v1/error/overview',
    element: <Navigate to="/base/errors" replace />
  }, // 46 pageviews → /base/errors
  {
    path: 'dashboard/v1/rating/trends',
    element: <Navigate to="/base/trends" replace />
  }, // 0 pageviews → /base/trends

  // List orders routes → /base/all-orders/*
  {
    path: 'list-orders',
    element: <Navigate to="/base/all-orders/overview" replace />
  },
  {
    path: 'list-orders/all',
    element: <Navigate to="/base/all-orders/overview" replace />
  }, // 426 pageviews → /base/all-orders/overview
  {
    path: 'list-orders/cancelled',
    element: <Navigate to="/base/all-orders/cancelled" replace />
  }, // 2 pageviews → /base/all-orders/cancelled
  {
    path: 'list-orders/inaccurate',
    element: <Navigate to="/base/all-orders/inaccurate" replace />
  }, // 1 pageview → /base/all-orders/inaccurate
  {
    path: 'list-orders/missed',
    element: <Navigate to="/base/all-orders/missed" replace />
  }, // 0 pageviews → /base/all-orders/missed

  // Redirects for /loop-reports
  {
    path: 'loop-reports',
    element: <Navigate to="/base/loop-reports" replace />
  },

  // Canonical routes (keep as-is)
  { path: 'finance/bank/integration', element: <PlaidAuth /> },

  { path: 'store-performance/intro', element: <StorePerformanceIntro /> },
  { path: 'store-performance/report-type', element: <StorePerformanceReport /> },
  { path: 'store-performance/subscribe-user', element: <StorePerformanceSubscribeUser /> },
  { path: 'store-performance/report-cadence', element: <StorePerformanceCadence /> },

  { path: 'drill-down/:name', element: <DrillDown /> },
  { path: 'changelog', element: <WhatsNew /> },
  { path: 'configurations', element: <Configurations /> },
  { path: 'configurations/store-availability', element: <Configurations /> },
  { path: 'configurations/store-availability/reasons', element: <StoreAvailabilityReasonsConfigs /> }
]

const moduleNamedRoutes = [
  {
    path: 'base',
    children: [
      { path: 'trends', element: <DashboardTrendsOnly /> },
      { path: 'metrics', element: <DashboardMetricsOnly /> },
      { path: 'errors', element: <DashboardErrorOverview /> },
      { path: 'benchmarks', element: <Benchmarks module={LoopModule.BASE} /> },
      { path: 'sales', element: <DashboardSales /> },
      { path: 'loop-reports', element: <ReportDownload /> },
      { path: 'compass-analytics', element: <CompassAnalytics /> },

      {
        path: 'all-orders',
        children: [
          { path: 'overview', element: <OrdersOld status="all" /> },
          { path: 'inaccurate', element: <OrdersOld status="inaccurate" /> },
          { path: 'inaccurate/:filter', element: <OrdersOld status="inaccurate" /> },
          { path: 'missed', element: <OrdersOld status="missed" /> },
          { path: 'missed/:filter', element: <OrdersOld status="missed" /> },
          { path: 'cancelled', element: <OrdersOld status="cancelled" /> },
          { path: 'cancelled/:filter', element: <OrdersOld status="cancelled" /> }
        ]
      },
      { path: 'error-insights',  element: <ErrorInsights />},
      { path: 'leaderboard', element: <Leaderboard /> }
    ]
  },
  {
    path: 'store-performance',
    children: [
      { path: 'intro', element: <StorePerformanceIntro /> },
      { path: 'report-type', element: <StorePerformanceReport /> },
      { path: 'subscribe-user', element: <StorePerformanceSubscribeUser /> },
      { path: 'report-cadence', element: <StorePerformanceCadence /> }
    ]
  },
  {
    path: 'olo',
    children: [
      {
        path: 'transactions',
        element: <OloTransactions />
      },
      {
        path: 'customers',
        element: <OloCustomers />
      },
      {
        path: 'items',
        element: <OloItems />
      }
    ]
  },
]

export const baseRoutes = [...existingRoutes, ...moduleNamedRoutes]
